import React, { useState, useEffect } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Button,
  Slider,
  TextField,
  Typography,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { ArrowDropUpOutlined } from "@mui/icons-material";
import StarRoundedIcon from "@mui/icons-material/StarRounded";
import Premium from "../../../assets/icons/premium.svg";
import "./MobilePlannersFilters.scss";
import { PlannersFilterState } from "../preneed-planners.types";
import { usePlannersFilter } from "../../../common/hooks/plannersUseFilter";

export type MobilePlannersFiltersProps = {
  initialFilters: PlannersFilterState;
  onChange: (filters: PlannersFilterState) => void;
  onCloseDrawer: () => void;
};

export default function MobilePlannersFilters({
  initialFilters,
  onChange,
  onCloseDrawer,
}: MobilePlannersFiltersProps) {
  // CONSTANTS
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // STATES
  const { filters, setFilters, resetFilters } = usePlannersFilter(
    initialFilters,
    onChange
  );

  const [activePriceField, setActivePriceField] = useState<
    "min" | "max" | null
  >(null);
  const [activeDistanceField, setActiveDistanceField] = useState<
    "min" | "max" | null
  >(null);

  const [priceSlider, setPriceSlider] = useState(0);
  const [distanceSlider, setDistanceSlider] = useState(0);
  const [priceTouched, setPriceTouched] = useState({ min: false, max: false });
  const [distanceTouched, setDistanceTouched] = useState({
    min: false,
    max: false,
  });

  useEffect(() => {
    if (initialFilters.priceRange) {
      setPriceTouched({ min: true, max: true });
    }
    if (initialFilters.distanceRange) {
      setDistanceTouched({ min: true, max: true });
    }
  }, [initialFilters]);

  const clampRange = (
    min: number,
    max: number,
    limit = 7000
  ): [number, number] => {
    if (min < 0) min = 0;
    if (max > limit) max = limit;

    if (min >= max) min = max;

    return [min, max];
  };

  return (
    <Box className="planners-filters">
      {/* Filters Header */}
      <Box className="planners-filters__header">
        <Typography variant="h6" className="planners-filters__title">
          Filters
        </Typography>
        <Button
          variant="text"
          onClick={() => {
            resetFilters();
            setPriceSlider(0);
            setDistanceSlider(0);
            setPriceTouched({ min: false, max: false });
            setDistanceTouched({ min: false, max: false });
          }}
          className="planners-filters__reset-btn"
        >
          Reset
        </Button>
      </Box>

      {/* ===== PRICE FILTER ===== */}
      <Accordion
        defaultExpanded
        className="planners-filters__accordion accordion-component"
      >
        <AccordionSummary
          expandIcon={
            <ArrowDropUpOutlined
              className="planners-filters__expand-icon"
              style={{ color: "#000" }}
            />
          }
          sx={{
            minHeight: "auto",
            "& .MuiAccordionSummary-content": { m: 0, mt: 1, mb: 1 },
            "& .MuiAccordionSummary-content.Mui-expanded": {
              m: 0,
              mt: 1,
              mb: 1,
            },
            borderBottom: "1px solid #e2e8f0",
          }}
        >
          <Typography className="filter-title planners-filters__accordion-title">
            Price
          </Typography>
        </AccordionSummary>

        <AccordionDetails>
          {/* Price Inputs */}
          <Box
            display="flex"
            gap={2}
            mb={1}
            className="planners-filters__range-inputs"
            mt={1}
            sx={{
              flexDirection: isMobile ? "column" : "row",
              gap: isMobile ? 1 : 2,
            }}
          >
            <TextField
              // label="Min"
              type="number"
              placeholder="Min"
              inputProps={{
                min: 0,
                max: 7000,
              }}
              value={
                priceTouched.min ? filters.priceRange?.[0] ?? "" : ""
              }
              onFocus={() => {
                setActivePriceField("min");
                if (filters.priceRange) {
                  setPriceSlider(filters.priceRange[0]);
                }
              }}
              onChange={(e) => {
                setPriceTouched((t) => ({ ...t, min: true }));
                let newMin = Number(e.target.value);
                const maxVal = filters.priceRange?.[1] ?? 7000;
                if (newMin > 7000) newMin = 7000;
                if (newMin < 0) newMin = 0;

                const [fixedMin, fixedMax] = clampRange(newMin, maxVal);

                setFilters((prev) => ({
                  ...prev,
                  priceRange: [fixedMin, fixedMax],
                }));
              }}
              size="small"
              sx={{
                "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button":
                {
                  display: "none",
                },
                "& input[type=number]": {
                  MozAppearance: "textfield",
                },
              }}
            />

            <TextField
              // label="Max"
              type="number"
              placeholder="Max"
              inputProps={{
                min: 0,
                max: 7000,
              }}
              value={
                priceTouched.max ? filters.priceRange?.[1] ?? "" : ""
              }
              onFocus={() => {
                setActivePriceField("max");
                if (filters.priceRange) {
                  setPriceSlider(filters.priceRange[1]);
                }
              }}
              onChange={(e) => {
                setPriceTouched((t) => ({ ...t, max: true }));
                let newMax = Number(e.target.value);
                const minVal = filters.priceRange?.[0] ?? 0;

                if (newMax > 7000) newMax = 7000;
                if (newMax < 0) newMax = 0;

                const [fixedMin, fixedMax] = clampRange(minVal, newMax);

                setFilters((prev) => ({
                  ...prev,
                  priceRange: [fixedMin, fixedMax],
                }));
              }}
              size="small"
              sx={{
                "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button":
                {
                  display: "none",
                },
                "& input[type=number]": {
                  MozAppearance: "textfield",
                },
              }}
            />
          </Box>

          <Box sx={{ px: 1, py: 1 }}>
            <Slider
              value={priceSlider}
              onChange={(_, val) => {
                const newVal = val as number;
                setFilters((prev) => {
                  let [minPrice, maxPrice] = prev.priceRange ?? [0, 7000];
                  if (activePriceField === "min") {
                    [minPrice, maxPrice] = clampRange(newVal, maxPrice);
                    setPriceTouched((t) => ({ ...t, min: true }));
                  } else {
                    [minPrice, maxPrice] = clampRange(minPrice, newVal);
                    setPriceTouched((t) => ({ ...t, max: true }));
                  }

                  return { ...prev, priceRange: [minPrice, maxPrice] };
                });

                setPriceSlider(newVal);
              }}
              valueLabelDisplay="auto"
              min={0}
              max={7000}
              step={100}
              className="planners-filters__slider"
              sx={{
                "& .MuiSlider-thumb": {
                  backgroundColor: "#fff",
                },
                "& .MuiSlider-rail": {
                  backgroundColor: "#e2e8f0",
                },
              }}
            />
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* ===== DISTANCE FILTER ===== */}
      <Accordion
        defaultExpanded
        className="planners-filters__accordion accordion-component"
      >
        <AccordionSummary
          expandIcon={
            <ArrowDropUpOutlined
              className="planners-filters__expand-icon"
              style={{ color: "#000" }}
            />
          }
          sx={{
            minHeight: "auto",
            "& .MuiAccordionSummary-content": { m: 0, mt: 1, mb: 1 },
            "& .MuiAccordionSummary-content.Mui-expanded": {
              m: 0,
              mt: 1,
              mb: 1,
            },
            borderBottom: "1px solid #e2e8f0",
          }}
        >
          <Typography className="filter-title planners-filters__accordion-title">
            Distance
          </Typography>
        </AccordionSummary>

        <AccordionDetails>
          {/* Distance Inputs */}
          <Box
            display="flex"
            gap={2}
            mb={1}
            className="planners-filters__range-inputs"
            mt={1}
            sx={{
              flexDirection: isMobile ? "column" : "row",
              gap: isMobile ? 1 : 2,
            }}
          >
            <TextField
              // label="Min"
              type="number"
              placeholder="Min"
              inputProps={{
                min: 0,
                max: 100,
              }}
              value={
                distanceTouched.min ? filters.distanceRange?.[0] ?? "" : ""
              }
              onFocus={() => {
                setActiveDistanceField("min");
                if (filters.distanceRange) {
                  setDistanceSlider(filters.distanceRange[0]);
                } else {
                  setDistanceSlider(0);
                }
              }}
              onChange={(e) => {
                setDistanceTouched((t) => ({ ...t, min: true }));
                let newMin = Number(e.target.value);
                const maxVal = filters.distanceRange?.[1] ?? 100;

                if (newMin > 100) newMin = 100;
                if (newMin < 0) newMin = 0;

                const [fixedMin, fixedMax] = clampRange(newMin, maxVal);

                setFilters((prev) => ({
                  ...prev,
                  distanceRange: [fixedMin, fixedMax],
                }));
              }}
              size="small"
              sx={{
                "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button":
                {
                  display: "none",
                },
                "& input[type=number]": {
                  MozAppearance: "textfield",
                },
              }}
            />

            <TextField
              // label="Max"
              type="number"
              placeholder="Max"
              inputProps={{
                min: 0,
                max: 100,
              }}
              value={
                distanceTouched.max ? filters.distanceRange?.[1] ?? "" : ""
              }
              onFocus={() => {
                setActiveDistanceField("max");
                if (filters.distanceRange) {
                  setDistanceSlider(filters.distanceRange[1]);
                } else {
                  setDistanceSlider(100);
                }
              }}
              onChange={(e) => {
                setDistanceTouched((t) => ({ ...t, max: true }));
                let newMax = Number(e.target.value);
                const minVal = filters.distanceRange?.[0] ?? 0;
                if (newMax > 100) newMax = 100;
                if (newMax < 0) newMax = 0;

                const [fixedMin, fixedMax] = clampRange(minVal, newMax);

                setFilters((prev) => ({
                  ...prev,
                  distanceRange: [fixedMin, fixedMax],
                }));
              }}
              size="small"
              sx={{
                "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button":
                {
                  display: "none",
                },
                "& input[type=number]": {
                  MozAppearance: "textfield",
                },
              }}
            />
          </Box>

          <Box sx={{ px: 1, py: 1 }}>
            <Slider
              value={distanceSlider}
              onChange={(_, val) => {
                const newVal = val as number;
                setFilters((prev) => {
                  let [minDist, maxDist] = prev.distanceRange ?? [0, 100];
                  if (activeDistanceField === "min") {
                    [minDist, maxDist] = clampRange(newVal, maxDist);
                    setDistanceTouched((t) => ({ ...t, min: true }));
                  } else {
                    [minDist, maxDist] = clampRange(minDist, newVal);
                    setDistanceTouched((t) => ({ ...t, max: true }));
                  }

                  return { ...prev, distanceRange: [minDist, maxDist] };
                });

                setDistanceSlider(newVal);
              }}
              valueLabelDisplay="auto"
              min={0}
              max={100}
              step={5}
              className="planners-filters__slider"
              sx={{
                "& .MuiSlider-thumb": {
                  backgroundColor: "#fff",
                },
                "& .MuiSlider-rail": {
                  backgroundColor: "#e2e8f0",
                },
              }}
            />
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* ===== STAR RATING FILTER ===== */}
      <Accordion
        defaultExpanded
        className="planners-filters__accordion accordion-component"
      >
        <AccordionSummary
          expandIcon={
            <ArrowDropUpOutlined
              className="planners-filters__expand-icon"
              style={{ color: "#000" }}
            />
          }
          sx={{
            minHeight: "auto",
            "& .MuiAccordionSummary-content": { m: 0, mt: 1, mb: 1 },
            "& .MuiAccordionSummary-content.Mui-expanded": {
              m: 0,
              mt: 1,
              mb: 1,
            },
            borderBottom: "1px solid #e2e8f0",
          }}
        >
          <Typography className="filter-title planners-filters__accordion-title">
            Star Rating
          </Typography>
        </AccordionSummary>

        <AccordionDetails
          sx={{
            padding: "6px",
            mt: 1,
          }}
        >
          <Box className="planners-filters__special-buttons">
            <Button
              sx={{
                border: "1px solid #BFC0C2",
                color:
                  filters.sortBy === "rating_high_to_low" ? "#fff" : "#4B4F50",
                bgcolor:
                  filters.sortBy === "rating_high_to_low"
                    ? "#ebd28d"
                    : "transparent",
                textTransform: "capitalize",
              }}
              endIcon={
                <StarRoundedIcon
                  sx={{
                    color:
                      filters.sortBy === "rating_high_to_low"
                        ? "9aa0a8"
                        : "rgba(3, 18, 36, 0.4)",
                  }}
                />
              }
              onClick={() =>
                setFilters((prev) => ({
                  ...prev,
                  sortBy:
                    prev.sortBy === "rating_high_to_low"
                      ? undefined
                      : "rating_high_to_low",
                }))
              }
              className="planners-filters__special-btn planners-filters__special-btn--toprated"
            >
              Top Rated
            </Button>

            <Button
              sx={{
                border: "1px solid #BFC0C2",
                color: "#4B4F50",
                textTransform: "capitalize",
              }}
              endIcon={
                <img
                  src={Premium}
                  alt="Premium Icon"
                  className="planners-filters__premium-icon"
                />
              }
              className="planners-filters__special-btn planners-filters__special-btn--premium"
            >
              Premium
            </Button>
          </Box>

          <Box className="planners-filters__rating-buttons">
            {[5, 4, 3].map((star) => {
              const isSelected = filters.ratings?.includes(star);
              return (
                <Button
                  key={star}
                  sx={{
                    border: "1px solid #BFC0C2",
                    color: isSelected ? "#fff" : "#4B4F50",
                    bgcolor: isSelected ? "#ebd28d" : "transparent",
                    padding: "5px 0px !important",
                    margin: "0px !important",
                  }}
                  onClick={() =>
                    setFilters((prev) => {
                      const current = prev.ratings || [];
                      return {
                        ...prev,
                        ratings: isSelected
                          ? current.filter((r) => r !== star)
                          : [...current, star],
                      };
                    })
                  }
                  endIcon={
                    <StarRoundedIcon
                      sx={{
                        color: isSelected ? "" : "#9aa0a8",
                      }}
                    />
                  }
                  className="planners-filters__rating-btn"
                >
                  {star}
                </Button>
              );
            })}
          </Box>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
}
